/**
 * IIFE Entry Point — Embeddable Chat Widget
 *
 * Reads data-* attributes from the <script> tag, creates a Shadow DOM container,
 * injects styles, and renders the Preact widget app.
 *
 * Usage:
 *   <script src="https://css-tutor-backend.onrender.com/widget.js"
 *     data-course-id="uuid"
 *     data-section-id="uuid"
 *     data-assignment-id="uuid"
 *     data-theme="light"
 *     data-position="bottom-right"></script>
 */
import { h, render } from "preact";
import { App } from "./App";
import type { WidgetProps } from "./types";
// CSS loaded as text string by esbuild (loader: { '.css': 'text' })
import widgetCss from "./styles/widget.css";

(function () {
  // Find the script tag that loaded us
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.error("[CSS Tutor Widget] Cannot find script element. Use a regular <script> tag, not async/defer module.");
    return;
  }

  // Read data attributes
  const widgetKey = script.getAttribute("data-key") || undefined;
  const courseId = script.getAttribute("data-course-id") || undefined;

  // Scope must come from SOMEWHERE: either data-key (server derives) or data-course-id.
  if (!widgetKey && !courseId) {
    console.error("[CSS Tutor Widget] data-key or data-course-id is required.");
    return;
  }

  const sectionId = script.getAttribute("data-section-id") || undefined;
  const assignmentId = script.getAttribute("data-assignment-id") || undefined;
  const theme = (script.getAttribute("data-theme") || "light") as "light" | "dark";
  const position = (script.getAttribute("data-position") || "bottom-right") as "bottom-right" | "bottom-left";
  const maxMessagesAttr = script.getAttribute("data-max-messages");
  const maxMessages = maxMessagesAttr ? parseInt(maxMessagesAttr, 10) : undefined;
  const color = script.getAttribute("data-color") || undefined;
  const logoUrl = script.getAttribute("data-logo") || undefined;
  const contentSelector = script.getAttribute("data-content-selector") || undefined;
  const pageContext = (script.getAttribute("data-page-context") || "on") as "on" | "off";

  // Derive API base URL from script src origin
  const scriptUrl = new URL(script.src);
  const apiBaseUrl = scriptUrl.origin;

  // Create container and Shadow DOM
  const container = document.createElement("div");
  container.id = "css-tutor-widget";
  container.style.cssText = "position:fixed;z-index:2147483647;pointer-events:none;top:0;left:0;width:0;height:0;";
  document.body.appendChild(container);

  const shadow = container.attachShadow({ mode: "closed" });

  // Inject styles into Shadow DOM
  const style = document.createElement("style");
  style.textContent = widgetCss;
  shadow.appendChild(style);

  // Apply theme class to shadow host
  if (theme === "dark") {
    container.classList.add("dark");
  }

  // Apply custom primary color
  if (color) {
    container.style.setProperty("--widget-primary", color);
  }

  // Create mount point inside Shadow DOM
  const mountPoint = document.createElement("div");
  mountPoint.style.cssText = "pointer-events:auto;";
  shadow.appendChild(mountPoint);

  // Render Preact app
  const props: WidgetProps = {
    apiBaseUrl,
    courseId,
    sectionId,
    assignmentId,
    widgetKey,
    theme,
    position,
    maxMessages,
    color,
    logoUrl,
    contentSelector,
    pageContext,
  };

  render(h(App, props), mountPoint);

  // ── Cleanup on SPA navigation ──────────────────────────────
  // MkDocs (and other SPA frameworks) swap page content without a full reload.
  // When the script tag is removed from the DOM, tear down the widget.
  function cleanup() {
    render(null, mountPoint);
    container.remove();
    observer.disconnect();
  }

  // Watch for our script tag being removed (MkDocs replaces the content area)
  const observer = new MutationObserver(() => {
    if (!document.contains(script)) {
      cleanup();
    }
  });

  // Observe the entire document for child removals
  observer.observe(document.body, { childList: true, subtree: true });
})();
